import React, { useMemo, useState } from 'react';
import {
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Clock3,
  Mail,
  Mic,
  Send,
  Sparkles,
  UserCheck,
} from 'lucide-react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';

const STATUS_BADGE_CLASSES = {
  urgent: 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/25 dark:bg-red-500/10 dark:text-red-300',
  attention: 'border-yellow-200 bg-yellow-50 text-yellow-700 dark:border-yellow-500/25 dark:bg-yellow-500/10 dark:text-yellow-300',
  next: 'border-aqua/30 bg-aqua/10 text-cyan-800 dark:border-aqua/25 dark:bg-aqua/10 dark:text-aqua',
  steady: 'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-400/20 dark:bg-slate-400/10 dark:text-slate-300',
};

const STAGE_THEME = {
  sand: {
    shell: 'border-[#3d342b] bg-[#171310]',
    top: 'border-b border-[#4b3f34] bg-[#211a15] text-[#e7d2b3]',
    count: 'bg-[#2a211a] text-[#e7d2b3]',
    chip: 'border-[#4b3f34] bg-[#241d17] text-[#e7d2b3]',
  },
  amber: {
    shell: 'border-[#4a3923] bg-[#17140f]',
    top: 'border-b border-[#5f4729] bg-[#241c13] text-[#f0c96b]',
    count: 'bg-[#2c2113] text-[#f0c96b]',
    chip: 'border-[#5f4729] bg-[#261d13] text-[#f0c96b]',
  },
  sky: {
    shell: 'border-[#31495b] bg-[#10161a]',
    top: 'border-b border-[#3a586f] bg-[#162028] text-[#9ec2da]',
    count: 'bg-[#1b2832] text-[#9ec2da]',
    chip: 'border-[#3a586f] bg-[#18242d] text-[#9ec2da]',
  },
  teal: {
    shell: 'border-[#35513d] bg-[#101512]',
    top: 'border-b border-[#41654c] bg-[#162019] text-[#9fc5a6]',
    count: 'bg-[#1c2a1f] text-[#9fc5a6]',
    chip: 'border-[#41654c] bg-[#18231b] text-[#9fc5a6]',
  },
  violet: {
    shell: 'border-[#4d3932] bg-[#151110]',
    top: 'border-b border-[#61483f] bg-[#201714] text-[#dfb6a6]',
    count: 'bg-[#291d19] text-[#dfb6a6]',
    chip: 'border-[#61483f] bg-[#241916] text-[#dfb6a6]',
  },
  green: {
    shell: 'border-[#2f4b39] bg-[#101510]',
    top: 'border-b border-[#3c6248] bg-[#152018] text-[#9fc4ac]',
    count: 'bg-[#1b281d] text-[#9fc4ac]',
    chip: 'border-[#3c6248] bg-[#17231a] text-[#9fc4ac]',
  },
};

const STEP_ICONS = {
  safe: Sparkles,
  email_1: Send,
  email_2: Mail,
  email_3: ArrowRight,
};

function StageBadge({ tone, children }) {
  return (
    <Badge className={STATUS_BADGE_CLASSES[tone] || STATUS_BADGE_CLASSES.steady}>
      {children}
    </Badge>
  );
}

function SequenceStat({ stat }) {
  const Icon = STEP_ICONS[stat.key] || Clock3;
  return (
    <div className={`rounded-[1.1rem] border p-4 ${STATUS_BADGE_CLASSES[stat.tone] || STATUS_BADGE_CLASSES.steady}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] opacity-70">
            {stat.label}
          </p>
          <p className="mt-3 text-3xl font-semibold">
            {stat.value}
          </p>
        </div>
        <div className="rounded-full bg-white/70 p-2.5 text-current dark:bg-black/20">
          <Icon className="h-4 w-4" />
        </div>
      </div>
      <p className="mt-3 text-sm leading-6 opacity-80">
        {stat.caption}
      </p>
    </div>
  );
}

function ColumnGuideItem({ item }) {
  return (
    <div className="rounded-2xl border border-white/70 bg-white/85 px-3 py-3 shadow-[0_18px_40px_-36px_rgba(15,23,42,0.45)] dark:border-white/10 dark:bg-black/20">
      <p className="text-sm font-semibold text-slate-900 dark:text-white">
        {item.title}
      </p>
      <p className="mt-1 text-xs leading-5 text-slate-600 dark:text-gray-300">
        {item.detail}
      </p>
    </div>
  );
}

function PipelineColumn({ column }) {
  const theme = STAGE_THEME[column.tone] || STAGE_THEME.sand;

  return (
    <Card className={`overflow-hidden p-0 shadow-[0_18px_45px_-34px_rgba(0,0,0,0.85)] ${theme.shell}`}>
      <div className={`border-b px-4 py-3 ${theme.top}`}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-semibold uppercase tracking-[0.24em] opacity-75">
              Stage
            </p>
            <h4 className="mt-2 text-lg font-semibold">
              {column.label}
            </h4>
            {typeof column.emphasisCount === 'number' ? (
              <div className="mt-3">
                <div className={`inline-flex max-w-full flex-col rounded-2xl border px-2.5 py-1.5 ${theme.chip}`}>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.12em] leading-tight">
                    {column.emphasisLabel}
                  </span>
                  <span className="mt-1 text-base font-semibold leading-none">
                    {column.emphasisCount}
                  </span>
                </div>
              </div>
            ) : null}
          </div>
          <div className="shrink-0">
            <div className={`inline-flex min-w-[3rem] items-center justify-center rounded-full px-3 py-1 text-base font-semibold ${theme.count}`}>
              {column.count}
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-4 px-4 py-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] ${theme.chip}`}>
            <UserCheck className="h-3.5 w-3.5" />
            {column.ownerLabel}
          </span>
          <StageBadge tone={column.ownerTone}>
            {column.nextAction}
          </StageBadge>
        </div>

        <p className="text-sm leading-6 text-gray-300">
          {column.description}
        </p>

        <div className="rounded-2xl border border-white/10 bg-black/15 px-3 py-3">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-500">
            What this means
          </p>
          <p className="mt-2 text-sm leading-6 text-gray-300">
            {column.helper}
          </p>
        </div>

        <div className="rounded-2xl border border-dashed border-white/10 bg-black/10 px-3 py-2.5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-gray-500">
            What you are seeing here
          </p>
          <p className="mt-2 text-sm leading-6 text-gray-300">
            {column.itemsLabel}
          </p>
        </div>

        <div className="space-y-2.5">
          {column.items.length ? (
            column.items.map((item) => (
              <ColumnGuideItem key={item.id} item={item} />
            ))
          ) : (
            <div className="rounded-2xl border border-dashed border-white/10 bg-black/10 px-3 py-5 text-center text-sm text-gray-500">
              Nothing is sitting here right now.
            </div>
          )}
        </div>
      </div>
    </Card>
  );
}

export default function GuidePartnersPipeline({ context }) {
  const [pipelineExpanded, setPipelineExpanded] = useState(() => Boolean(context.pipelineDefaultExpanded));
  const overviewChips = useMemo(
    () => (context.stageColumns || []).map((column) => ({
      key: column.key,
      label: column.label,
      count: column.count,
      emphasisCount: column.emphasisCount,
      emphasisLabel: column.emphasisLabel,
      ownerTone: column.ownerTone,
    })),
    [context.stageColumns]
  );

  return (
    <div className="grid gap-4">
      <Card className="border-[#2f3139] bg-gradient-to-br from-[#1d1b18] via-[#171718] to-[#121518] p-5">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
            <div className="max-w-3xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-amber-300">
                Guide Partners operator room
              </p>
              <h3 className="mt-3 text-2xl font-semibold leading-8 text-white">
                {context.startTitle}
              </h3>
              <p className="mt-3 text-sm leading-6 text-gray-300">
                {context.startDetail}
              </p>
            </div>
            <StageBadge tone={context.statusTone}>
              {context.statusLabel}
            </StageBadge>
          </div>

          <div className="grid gap-2 md:grid-cols-2">
            {(context.signals || []).map((signal, index) => (
              <div key={`${signal.text}-${index}`} className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3">
                <p className="text-sm leading-6 text-gray-300">
                  {signal.text}
                </p>
              </div>
            ))}
          </div>

          {(context.priorityItems || []).length ? (
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-slate-500 dark:text-gray-500">
                Your move
              </p>
              <div className="mt-3 grid gap-3 xl:grid-cols-3">
                {context.priorityItems.map((item, index) => (
                  <div key={`${item.title}-${index}`} className={`rounded-[1.1rem] border p-4 ${STATUS_BADGE_CLASSES[item.tone] || STATUS_BADGE_CLASSES.steady}`}>
                    <p className="text-[11px] font-semibold uppercase tracking-[0.24em] opacity-75">
                      {item.eyebrow}
                    </p>
                    <h4 className="mt-3 text-base font-semibold">
                      {item.title}
                    </h4>
                    <p className="mt-2 text-sm leading-6 opacity-85">
                      {item.detail}
                    </p>
                    <p className="mt-4 text-[11px] font-semibold uppercase tracking-[0.18em] opacity-70">
                      {item.location}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </Card>

      <div className="grid gap-3 xl:grid-cols-4">
        {(context.sequenceStats || []).map((stat) => (
          <SequenceStat key={stat.key} stat={stat} />
        ))}
      </div>

      <Card className="border-[#2a2c34] bg-[#131517] p-4">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2">
              <Mic className="h-4 w-4 text-amber-300" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-500">
                Pipeline Overview
              </p>
            </div>
            <h4 className="mt-3 text-base font-semibold text-white">
              Keep the full stage board nearby without letting it hijack the page
            </h4>
            <p className="mt-2 text-sm leading-6 text-gray-300">
              The detailed pipeline is available when you need stage-by-stage review. Most days, keep this collapsed and work from the operator room and Kevin Next Moves first.
            </p>
          </div>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            <Button
              variant="outline"
              onClick={() => setPipelineExpanded((current) => !current)}
              className="text-foreground"
            >
              {pipelineExpanded ? <ChevronUp className="mr-2 h-4 w-4" /> : <ChevronDown className="mr-2 h-4 w-4" />}
              {pipelineExpanded ? 'Hide detailed pipeline' : 'Show detailed pipeline'}
            </Button>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {overviewChips.map((chip) => (
            <div
              key={chip.key}
              className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-2 text-sm text-gray-200"
            >
              <span className="font-medium">{chip.label}</span>
              <span className="ml-2 rounded-full bg-black/20 px-2 py-0.5 text-xs text-white">
                {chip.count}
              </span>
              {typeof chip.emphasisCount === 'number' && chip.emphasisCount > 0 ? (
                <span className={`ml-2 inline-flex items-center rounded-full px-2 py-0.5 text-[11px] ${
                  STATUS_BADGE_CLASSES[chip.ownerTone] || STATUS_BADGE_CLASSES.steady
                }`}>
                  {chip.emphasisLabel}: {chip.emphasisCount}
                </span>
              ) : null}
            </div>
          ))}
        </div>

        {pipelineExpanded ? (
          <div className="mt-4 space-y-3 border-t border-white/10 pt-4">
            {(context.watchListItems || []).length ? (
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="flex items-center gap-2">
                  <Mic className="h-4 w-4 text-amber-300" />
                  <p className="text-[11px] font-semibold uppercase tracking-[0.24em] text-gray-500">
                    What needs attention across stages
                  </p>
                </div>
                <div className="mt-3 grid gap-2 xl:grid-cols-5">
                  {context.watchListItems.map((item) => (
                    <div key={item.id} className="rounded-2xl border border-white/10 bg-white/[0.03] px-3 py-3">
                      {item.stageLabel ? (
                        <div className="mb-2">
                          <StageBadge tone={item.stageTone}>
                            {item.stageLabel}
                          </StageBadge>
                        </div>
                      ) : null}
                      <p className="text-sm font-medium text-white">
                        {item.title}
                      </p>
                      <p className="mt-1 text-xs leading-5 text-gray-400">
                        {item.detail}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}

            <div className="grid gap-3 md:grid-cols-2 2xl:grid-cols-6">
              {(context.stageColumns || []).map((column) => (
                <PipelineColumn key={column.key} column={column} />
              ))}
            </div>
          </div>
        ) : null}
      </Card>
    </div>
  );
}
